import { Component, Input, OnInit, Output, EventEmitter, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AmbassadorCalendarComponent } from './ambassador-calendar/ambassador-calendar.component';
import { AmbassadorCalendarSummaryComponent } from './ambassador-calendar-summary/ambassador-calendar-summary.component';
import { AmbassadorReservationFormComponent } from './ambassador-reservation-form/ambassador-reservation-form.component';
import { AmbassadorReservationConfirmationComponent } from './ambassador-reservation-confirmation/ambassador-reservation-confirmation.component';
import { AmbassadorProgramCompanyUserService } from '../../../services/ambassador-program-company-user.service';

@Component({
    selector: 'ambassador-reservation',
  standalone: true,
  templateUrl: './ambassador-reservation.component.html',
  imports: [CommonModule,
    AmbassadorCalendarComponent,
    AmbassadorCalendarSummaryComponent,
    AmbassadorReservationFormComponent,
    AmbassadorReservationConfirmationComponent]
})

export class AmbassadorReservationComponent implements OnInit {

    @Input() idVehicle: string;

    showSummary = signal<boolean>(false);
    showForm = signal<boolean>(false);
    showConfirmation = signal<boolean>(false);
    isConfirmed = signal<boolean>(false);
    isCancelled = signal<boolean>(false);
    idSchedule = signal<string>('');
    timePreference = signal<string>('');

    // Outputs para exponer los estados al contenedor padre
    @Output() showSummaryChange = new EventEmitter<boolean>();
    @Output() showFormChange = new EventEmitter<boolean>();
    @Output() showConfirmationChange = new EventEmitter<boolean>();
    @Output() isConfirmedChange = new EventEmitter<boolean>();
    @Output() isCancelledChange = new EventEmitter<boolean>();
    @Output() reservationStateChange = new EventEmitter<{ showSummary: boolean; showForm: boolean; showConfirmation: boolean; showConfirmed: boolean; showCancelled: boolean }>();

    constructor(
        private userService: AmbassadorProgramCompanyUserService
    ) {
        // Efecto para emitir cambios de estado al contenedor padre
        // Se ejecuta automáticamente cuando cambian los signals
        effect(() => {
            const showSummary = this.showSummary();
            const showForm = this.showForm();
            const showConfirmation = this.showConfirmation();
            const isConfirmed = this.isConfirmed();
            const isCancelled = this.isCancelled();

            const state = {
                showSummary,
                showForm,
                showConfirmation,
                showConfirmed: isConfirmed,
                showCancelled: isCancelled
            };

            // Emitir eventos individuales
            this.showSummaryChange.emit(showSummary);
            this.showFormChange.emit(showForm);
            this.showConfirmationChange.emit(showConfirmation);
            this.isConfirmedChange.emit(isConfirmed);
            this.isCancelledChange.emit(isCancelled);

            // Emitir evento combinado con todos los estados
            this.reservationStateChange.emit(state);
        });
    }

    ngOnInit() {
        this.checkExistingReservation();
    }

    private checkExistingReservation() {
        this.userService.getProgramCompanyUserData().subscribe(userData => {
            if (userData) {
                if (userData.IdReservationSchedule) {
                    // El usuario ya tiene una reserva, obtener los datos y mostrar el componente de confirmación
                    this.idSchedule.set(userData.IdReservationSchedule);
                    if (userData.TimePreference) {
                        this.timePreference.set(userData.TimePreference);
                    }
                    this.showConfirmation.set(true);
                    this.showForm.set(false);
                    this.showSummary.set(false);
                }
            }
            // Emitir el estado inicial después de completar la verificación
            // Esto asegura que el estado se sincronice incluso si el listener se configura después
            setTimeout(() => {
                this.emitCurrentState();
            }, 100);
        });
    }

    /**
     * Emite el estado actual del componente
     * Útil para sincronizar el estado cuando se configura el listener externo
     */
    private emitCurrentState(): void {
        const state = this.currentReservationState;
        this.reservationStateChange.emit(state);
    }

    onReservationRequested(event: { idSchedule: string; timePreference: string }) {
        this.idSchedule.set(event.idSchedule);
        this.timePreference.set(event.timePreference);
        this.showSummary.set(true);
        this.showForm.set(false);
    }

    onBackToCalendar() {
        this.showSummary.set(false);
        this.showForm.set(false);
    }

    onConfirmReservation() {
        this.showForm.set(true);
        this.showConfirmation.set(false);
    }

    onReservationCompleted() {
        this.showForm.set(false);
        this.showConfirmation.set(true);
    }

    // Getters públicos para acceso directo a los estados desde el contenedor padre
    get currentShowSummary(): boolean {
        return this.showSummary();
    }

    get currentShowForm(): boolean {
        return this.showForm();
    }

    get currentShowConfirmation(): boolean {
        return this.showConfirmation();
    }

    get currentReservationState(): { showSummary: boolean; showForm: boolean; showConfirmation: boolean; showConfirmed: boolean; showCancelled: boolean } {
        return {
            showSummary: this.showSummary(),
            showForm: this.showForm(),
            showConfirmation: this.showConfirmation(),
            showConfirmed: this.isConfirmed(),
            showCancelled: this.isCancelled()
        };
    }

    /**
     * Método público para obtener el estado actual de la reserva
     * Útil para sincronizar el estado cuando se configura el listener externo
     */
    public getReservationState(): { showSummary: boolean; showForm: boolean; showConfirmation: boolean; showConfirmed: boolean; showCancelled: boolean } {
        return this.currentReservationState;
    }

    onConfirmationStateChange(event: { showConfirmed: boolean; showCancelled: boolean }) {
        this.isConfirmed.set(event.showConfirmed);
        this.isCancelled.set(event.showCancelled);
        // Forzar la emisión del estado después de actualizar los signals
        // Usar queueMicrotask para asegurar que se ejecute después del ciclo actual
        // Esto asegura que el efecto se haya ejecutado y el estado se propague correctamente
        queueMicrotask(() => {
            this.emitCurrentState();
        });
    }

}

